import {
  useCurrentWorkspace,
  useModifyWorkspace,
  useWorkspaceKeysStore,
} from '@easy-expense/data-firestore-client';
import { getTranslation } from '@easy-expense/intl-client';
import { theme } from '@easy-expense/ui-theme';
import { Layout, OpenSans, Spacer } from '@easy-expense/ui-web-core';
import { Checkbox } from '@mui/material';
import React from 'react';

/** Width of the label column every workspace setting row uses, so checkboxes line up. */
const LABEL_WIDTH = 350;
/** Left padding the `px` prop gives a row (theme size `xs-12`). */
const ROW_PADDING = 12;
/** Left padding of the child row, so it reads as belonging to the setting above it. */
const CHILD_INDENT = 40;

export const WorkspaceReimbursableSetting: React.FC = () => {
  const workspace = useCurrentWorkspace();

  const currentWorkspacePath = useWorkspaceKeysStore((s) => s.currentWorkspacePath());
  const [updateWorkspace] = useModifyWorkspace(currentWorkspacePath, 'update');

  function onChange(showReimbursementsToggle: boolean) {
    if (!workspace?.settings) {
      return;
    }
    updateWorkspace({
      settings: {
        ...workspace.settings,
        showReimbursementsToggle,
      },
    });
  }

  function onChangeDefault(reimbursableByDefault: boolean) {
    if (!workspace?.settings) {
      return;
    }
    updateWorkspace({
      settings: {
        ...workspace.settings,
        reimbursableByDefault,
      },
    });
  }

  const showsReimbursements = !!workspace?.settings.showReimbursementsToggle;

  return (
    <Layout.Column
      style={{
        borderTop: `1px solid ${theme.colors.inputBorder}`,
      }}
    >
      <Layout.Row align px py>
        <Layout.Column
          style={{
            maxWidth: LABEL_WIDTH,
            width: '100%',
          }}
        >
          <Layout.Row align>
            <OpenSans.Primary size={16} weight="bold-700">
              {getTranslation('Show Reimbursable on Expenses')}
            </OpenSans.Primary>
          </Layout.Row>
          <Layout.Row align>
            <OpenSans.Secondary size={16}>
              {getTranslation('Add toggle to mark expenses as reimbursable')}
            </OpenSans.Secondary>
          </Layout.Row>
        </Layout.Column>
        <Spacer.Horizontal size={24} />
        <Layout.Column>
          <Checkbox
            style={{ padding: 0 }}
            checked={showsReimbursements}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const show = event.target.checked;
              onChange(show);
            }}
          />
        </Layout.Column>
      </Layout.Row>

      {showsReimbursements ? (
        <Layout.Row align px py style={{ paddingLeft: CHILD_INDENT }}>
          <Layout.Column
            style={{
              // Narrower by the extra indent, so its checkbox lands in the same column as the rest.
              maxWidth: LABEL_WIDTH - (CHILD_INDENT - ROW_PADDING),
              width: '100%',
            }}
          >
            <Layout.Row align>
              <OpenSans.Primary size={16} weight="bold-700">
                {getTranslation('Reimbursable by default')}
              </OpenSans.Primary>
            </Layout.Row>
            <Layout.Row align>
              <OpenSans.Secondary size={16}>
                {getTranslation('New expenses are marked as reimbursable')}
              </OpenSans.Secondary>
            </Layout.Row>
          </Layout.Column>
          <Spacer.Horizontal size={24} />
          <Layout.Column>
            <Checkbox
              style={{ padding: 0 }}
              checked={!!workspace?.settings.reimbursableByDefault}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                onChangeDefault(event.target.checked);
              }}
            />
          </Layout.Column>
        </Layout.Row>
      ) : null}
    </Layout.Column>
  );
};
